import React, { useState, useEffect, useRef } from 'react';
import { Box, CircularProgress, Alert, Typography } from '@mui/material';
import ReactPlayerLoader from '@brightcove/react-player-loader';
import { parseBrightcoveUrl } from '../_utils/brightcoveHelper';

interface BrightcovePlayerProps {
  videoUrl: string;
  autoplay?: boolean;
  autoplayDelay?: number; // Delay in ms before autoplay starts
  muted?: boolean;
  onPlayerReady?: (player: any) => void;
  onPlayerError?: (error: any) => void;
}

const BrightcovePlayer: React.FC<BrightcovePlayerProps> = ({
  videoUrl,
  autoplay = false,
  autoplayDelay = 500,
  muted: mutedProp,
  onPlayerReady,
  onPlayerError,
}) => {
  // Autoplay requires muted in modern browsers
  const muted = autoplay ? true : (mutedProp ?? false);
  const [error, setError] = useState<string | null>(null);
  const [playerConfig, setPlayerConfig] = useState<{
    accountId: string;
    playerId: string;
    embedId?: string;
    videoId: string;
  } | null>(null);
  const [isInView, setIsInView] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<any>(null);
  const hasAutoPlayedRef = useRef(false);

  // Parse video URL
  useEffect(() => {
    if (!videoUrl) {
      setError('No video URL provided');
      return;
    }

    try {
      const parsed = parseBrightcoveUrl(videoUrl);

      if (!parsed) {
        setError('Invalid Brightcove URL');
        return;
      }

      setPlayerConfig(parsed);
      setError(null);
      // Reset autoplay flag when video URL changes
      hasAutoPlayedRef.current = false;
    } catch (err) {
      setError(`Error parsing video URL: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }, [videoUrl]);

  // Set up Intersection Observer to detect when player is in view
  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          console.log('Intersection change:', {
            isIntersecting: entry.isIntersecting,
            intersectionRatio: entry.intersectionRatio,
          });
          setIsInView(entry.isIntersecting);
        });
      },
      {
        threshold: 0.7, // Trigger when 70% of the player is visible
      }
    );

    const currentContainer = containerRef.current;
    observer.observe(currentContainer);

    // Check initial intersection state
    // This handles the case where the element is already in view when observer starts
    const checkInitialIntersection = () => {
      if (currentContainer) {
        const rect = currentContainer.getBoundingClientRect();
        const windowHeight = window.innerHeight || document.documentElement.clientHeight;
        const windowWidth = window.innerWidth || document.documentElement.clientWidth;

        // Check if at least 70% is visible
        const visibleHeight = Math.min(rect.bottom, windowHeight) - Math.max(rect.top, 0);
        const visibleWidth = Math.min(rect.right, windowWidth) - Math.max(rect.left, 0);
        const visibleArea = visibleHeight * visibleWidth;
        const totalArea = rect.height * rect.width;

        const visibilityRatio = totalArea > 0 ? visibleArea / totalArea : 0;
        console.log('Initial intersection check:', { visibilityRatio, isVisible: visibilityRatio >= 0.7 });

        if (visibilityRatio >= 0.7) {
          setIsInView(true);
        }
      }
    };

    // Small delay to ensure DOM is ready
    setTimeout(checkInitialIntersection, 50);

    return () => {
      observer.disconnect();
    };
  }, []);

  // Handle autoplay when player is in view
  useEffect(() => {
    console.log('Autoplay effect:', {
      autoplay,
      isInView,
      hasPlayer: !!playerRef.current,
      hasAutoPlayed: hasAutoPlayedRef.current,
    });

    if (autoplay && isInView && playerRef.current && !hasAutoPlayedRef.current) {
      // Add configurable delay before autoplay (plus a small delay to ensure player is ready)
      const totalDelay = autoplayDelay + 100;
      console.log(`Scheduling autoplay in ${totalDelay}ms`);

      const playTimeout = setTimeout(() => {
        try {
          if (playerRef.current && typeof playerRef.current.play === 'function') {
            console.log('Attempting to play video...');
            const playPromise = playerRef.current.play();

            // Handle the promise if it exists (modern browsers)
            if (playPromise !== undefined) {
              playPromise
                .then(() => {
                  console.log('Video autoplay successful');
                  hasAutoPlayedRef.current = true;
                })
                .catch((err: any) => {
                  console.error('Failed to autoplay video:', err);
                });
            } else {
              hasAutoPlayedRef.current = true;
            }
          } else {
            console.warn('Player or play function not available');
          }
        } catch (err) {
          console.error('Failed to autoplay video:', err);
        }
      }, totalDelay);

      return () => clearTimeout(playTimeout);
    }
  }, [autoplay, autoplayDelay, isInView]);

  if (error) {
    return (
      <Alert severity='error'>
        <Typography variant='body2'>{error}</Typography>
      </Alert>
    );
  }

  if (!playerConfig) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 300,
          width: '100%',
          backgroundColor: '#f5f5f5',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        width: '100%',
        backgroundColor: '#000',
      }}
    >
      <ReactPlayerLoader
        accountId={playerConfig.accountId}
        playerId={playerConfig.playerId}
        embedId={playerConfig.embedId}
        videoId={playerConfig.videoId}
        options={{
          autoplay: false, // We handle autoplay manually based on visibility
          muted: muted,
          controls: true,
          responsive: true,
          fluid: true,
        }}
        onSuccess={(success: any) => {
          console.log('Brightcove player loaded successfully');
          playerRef.current = success.ref;

          // Ensure muted state is set on the player if needed
          if (muted && success.ref && typeof success.ref.muted === 'function') {
            success.ref.muted(true);
            console.log('Player muted for autoplay');
          }

          onPlayerReady?.(success.ref);
        }}
        onFailure={(error: any) => {
          setError(`Failed to load video: ${error.message || 'Unknown error'}`);
          onPlayerError?.(error);
        }}
      />
    </Box>
  );
};

export default BrightcovePlayer;
